// src/components/ProtectedRoute.js
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Spinner, Card, Button } from "react-bootstrap";
import { Lock } from "lucide-react";
import api from "../services/api";

/**
 * ProtectedRoute — wraps dashboard routes.
 * - Redirects to login if not authenticated.
 * - Shows loading spinner while auth state is initializing.
 * - Optionally checks onboarding & policy compliance.
 * - Optionally restricts by role(s).
 */
const ProtectedRoute = ({ children, roles, checkOnboarding = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  const [onboardingChecked, setOnboardingChecked] = useState(!checkOnboarding);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    if (!checkOnboarding || !user || user.isLocked) {
      setOnboardingChecked(true);
      return;
    }

    let isMounted = true;
    api.get("/onboarding/status")
      .then((res) => {
        if (isMounted) {
          if (!res.data.onboardingCompleted || res.data.needsUpdate) {
            setNeedsOnboarding(true);
          } else {
            setNeedsOnboarding(false);
          }
        }
      })
      .catch((err) => {
        if (isMounted) {
          // If server returns 403 ONBOARDING_REQUIRED
          if (err?.response?.data?.code === "ONBOARDING_REQUIRED") {
            setNeedsOnboarding(true);
          } else {
            setNeedsOnboarding(false);
          }
        }
      })
      .finally(() => {
        if (isMounted) {
          setOnboardingChecked(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [checkOnboarding, user, location.pathname]);

  // Still checking auth state or onboarding status
  if (loading || (!onboardingChecked && checkOnboarding)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh", background: "#0c0d11" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Account locked
  if (user.isLocked) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "100vh", backgroundColor: "#111827" }}>
        <Card className="text-center shadow" style={{ maxWidth: 400, width: "90%", border: "1px solid #374151", backgroundColor: "#1f2937", color: "#f3f4f6" }}>
          <Card.Body className="py-5">
            <div className="mb-4 d-flex justify-content-center text-danger">
              <Lock size={64} />
            </div>
            <Card.Title as="h3" className="mb-3">Account Locked</Card.Title>
            <Card.Text className="text-muted mb-4">
              Your access to the CRM has been temporarily locked. Please contact your manager or administrator for assistance.
            </Card.Text>
            <Button variant="outline-light" onClick={() => (window.location.href = "/")}>
              Return to Login
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Mandatory Onboarding & Policies check
  if (checkOnboarding && needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Role restriction
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
